import { Observable, of } from 'rxjs';
import { Entity } from '../objects/entity';
import { Party } from '../objects/party';
import { EntityType } from '../objects/interface-library';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ChangeDetectorRef, inject } from '@angular/core';
import { DocumentViewerResult, DocumentViewerService } from '../service/document-viewer.service';


export class CreateEntity extends Entity {
    public sanitizer = inject(DomSanitizer);
    protected documentViewerService = inject(DocumentViewerService);
    /**
     * The app is ZONELESS (main.ts → provideZonelessChangeDetection). Assigning a
     * plain field after an `await` does not notify Angular, so the view keeps
     * rendering the pre-await state until some unrelated event happens to trigger
     * change detection. `processDocument()` is async (it mints a presigned URL),
     * which is exactly that shape — symptom was the document "view" (eyeball)
     * button staying disabled until the user clicked somewhere on the page.
     */
    protected cdrZoneless = inject(ChangeDetectorRef);
    showDocumentViewer = false;

    contractFileUrl: any = null;
    currentDocument: DocumentViewerResult | null = null;

    public unsafeContractUrl: string | null = null;

    public EntityType = EntityType;

    newPartyName: string | null = null;

    // States should be saved in a better way
    replaceContract: boolean = false;
    editContract: boolean = false;
    replaceBaa: boolean = false;
    editBaa: boolean = false;
    replaceCoi: boolean = false;
    editCoi: boolean = false;

    saving: boolean = false;

    // File upload properties
    uploadedFile: File | null = null;
    isDragOver = false;

    constructor() {
        super();
    }


    public saveNewPartySimplified(partyName: string | null): Observable<Party | null> {
        if (partyName) {
        // The type guard ensures partyName is a string here.
        // This path is correct as it returns the Observable from the service.
        return this.partyService.saveNewPartySimplified(partyName);
        } else {
        // This is the "bail out" path.
        // We explicitly return an Observable that emits null and then completes.
        console.warn('Cannot save party: name is null or empty.');
        return of(null);
        }
    }


    addedOtherParty() {
        console.log('*** addedOtherParty *** impl');
    }

    onSelectLegalEntity() {
        console.log('*** onSelectLegalEntity *** impl');
    }

    processIntoRawText() {
        console.log('*** processIntoRawText *** impl');
    }

    /**
     * Process uploaded or existing file
     */
    async processFile(fileUrlOrAttachment: string | any): Promise<void> {
        this.currentDocument = await this.documentViewerService.processDocument(fileUrlOrAttachment);
        this.contractFileUrl = this.currentDocument.directLink;
        this.cdrZoneless.markForCheck();   // zoneless: post-await assignment needs an explicit notify
    }

    // Keep your existing methods but delegate to service
    getFileType(url?: string | null): 'pdf' | 'docx' | 'xlsx' | 'pptx' | null {
        if (!this.currentDocument && url) {
        void this.processFile(url);
        }
        return this.currentDocument?.fileType as 'pdf' | 'docx' | 'xlsx' | 'pptx' || null;
    }


    downloadFile(url?: string): void {
        // fetchUrl, not directLink — the bucket is private, so only a presigned url resolves.
        const fileUrl = url || this.currentDocument?.fetchUrl;
        if (fileUrl) {
        this.documentViewerService.downloadFile(fileUrl);
        }
    }

    /**
     * Process uploaded or existing file
     */
    async loadDocument(fileUrlOrAttachment: string | any): Promise<void> {
        if (!fileUrlOrAttachment) {
        this.currentDocument = null;
        this.cdrZoneless.markForCheck();
        return;
        }
        this.currentDocument = await this.documentViewerService.processDocument(fileUrlOrAttachment);
        this.contractFileUrl = this.currentDocument.directLink;
        this.cdrZoneless.markForCheck();   // zoneless: post-await assignment needs an explicit notify
        console.log('[CreateEntity.loadDocument] input:', fileUrlOrAttachment,
          '→ directLink:', this.currentDocument.directLink,
          '| fileType:', this.currentDocument.fileType,
          '| isBlob:', this.currentDocument.directLink?.startsWith('blob:'));
    }

    /**
     * Toggle document viewer visibility
     */
    toggleDocumentViewer(): void {
        this.showDocumentViewer = !this.showDocumentViewer;
    }

    /**
     * Download current document
     */
    downloadDocument(): void {
        if (this.currentDocument) {
        this.documentViewerService.downloadFile(
            this.currentDocument.fetchUrl,
            this.currentDocument.fileName
        );
        }
    }

    /**
     * Open current document in new tab
     */
    openDocumentInNewTab(): void {
        if (this.currentDocument) {
        this.documentViewerService.openInNewTab(this.currentDocument.fetchUrl);
        }
    }


    getViewerUrl(): string {
        return this.currentDocument?.fetchUrl || '';
    }

    // ==========================================
    // FILE UPLOAD METHODS (Shared across Contract, BAA, COI)
    // ==========================================

    /**
     * Handle drag over event for file upload
     */
    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    /**
     * Handle drag leave event for file upload
     */
    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    /**
     * Handle drop event for file upload
     */
    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.handleFileUpload(files[0]);
        }
    }

    /**
     * Handle file selected from file input
     */
    onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.handleFileUpload(file);
        }
    }

    /**
     * Process and validate uploaded file
     * IMPORTANT: This is the CORRECT implementation that passes both the blob URL
     * and the original filename to loadDocument(). This ensures the DocumentViewerService
     * can properly detect the file type (PDF vs DOCX) even with blob URLs.
     */
    handleFileUpload(file: File): void {
        const allowedTypes = [
            'application/pdf',
            'application/msword',                                                         // DOC
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // DOCX
            'application/vnd.ms-excel',                                                   // XLS
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',         // XLSX
            'application/vnd.ms-powerpoint',                                              // PPT
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'  // PPTX
        ];

        if (!allowedTypes.includes(file.type)) {
            alert('Please upload a PDF, Word, Excel, or PowerPoint file');
            return;
        }

        this.uploadedFile = file;
        this.setMainDocumentName(file.name);
        const fileUrl = URL.createObjectURL(file);

        // Pass an object with both the blob URL and the original filename
        // This ensures the document viewer can correctly detect the file type
        this.loadDocument({
            directLink: fileUrl,
            originalFileName: file.name
        });

        this.showDocumentViewer = true;
    }

    /**
     * Remove the currently uploaded file
     */
    removeFile(): void {
        this.uploadedFile = null;
        this.setMainDocumentName('');
        this.currentDocument = null;
        this.showDocumentViewer = false;
    }

    /**
     * Fill the read-only "Main Document Name" field with the uploaded file's own name.
     * A form control rather than a save-time stamp so it shows as soon as the file is
     * added, and so an edit round-trips it (the forms save from form.value). Each
     * subclass owns its `form`; one without the control is simply left alone.
     */
    private setMainDocumentName(name: string): void {
        (this as any).form?.get?.('mainDocumentName')?.setValue(name);
    }

    /**
     * Extract filename from URL
     */
    getFileNameFromUrl(url: string | null): string {
        if (!url) return 'Unknown file';
        return url.split('/').pop() || 'Document file';
    }

}
